import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { canAccessModuleGuard } from '@iris/common/guards/can-access-module.guard';
import { canConfigModuleGuard } from '@iris/common/guards/can-config-module.guard';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisFeatureFlags } from '@iris/common/modules/feature-flags/IrisFeatureFlags';
import { isUserAuthorizedGuard } from '@iris/modules/auth/is-user-authorized.guard';
import { LABEL_MARKETPLACE } from '@iris/modules/marketplace/common/constants/marketplace.constants';
import { featureEnabled } from '@karelics/angular-unleash-proxy-client';
import { TranslateService } from '@ngx-translate/core';
import { AppComponent } from 'app/app.component';
import { irisTranslationServiceProxy } from '@iris/common/utils/iris-translation-service.proxy';
import { StartPageComponent } from './app-start-page.component';
import { AppAliasUrlSerializer } from '@iris/common/services/app-alias-url-serializer';
import { applicationExistsGuard } from '@iris/common/guards/application-exists.guard';
import { syncSavedAppAliasGuard } from './common/guards/sync-saved-app-alias.can-activate.guard';

const ROUTES_AFTER_LOGIN: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: StartPageComponent,
  },
  {
    path: 'profile',
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/profile/profile.module').then(m => m.IrisProfileModule),
    title: 'label.UserProfile',
  },
  {
    path: 'projects',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/projects/projects.module').then(m => m.ProjectsModule),
    title: 'label.ProjectManagement',
  },
  {
    path: 'plc-errors',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/plc-messages/plc-messages.routes').then(m => m.PLC_MESSAGES_ROUTES),
    title: 'label.PLCErrors',
  },
  {
    path: 'resource-planning',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/resource-planning/resource-planning.module').then(m => m.ResourcePlanningModule),
    data: { moduleCode: IrisModules.RESOURCE_PLANNING },
    title: 'label.ResourcePlanning',
  },
  {
    path: 'personnel-management',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/personnel-management/personnel-management.module').then(m => m.IrisPersonnelManagementModule),
    data: { moduleCode: IrisModules.PERSONNEL_MANAGEMENT },
  },
  {
    path: 'devices',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/devices/devices.module').then(m => m.DevicesModule),
    data: { moduleCode: IrisModules.DEVICE_DATA },
    title: 'label.Devices',
  },
  {
    path: 'marketplace',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import ('./iris-modules/marketplace/marketplace.module').then(m => m.MarketplaceModule),
    title: LABEL_MARKETPLACE,
  },
  {
    path: 'sensors',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/sensors/sensors.module').then(m => m.SensorsModule),
    data: { moduleName: 'label.Sensors' },
    title: 'label.Sensors',
  },
  {
    path: 'dataseries',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/dataseries/dataseries.module').then(m => m.DataseriesModule),
    title: 'label.DataSeries',
  },
  {
    path: 'data-visualization',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('@iris/modules/data-visualization/data-visualization.module').then(m => m.IrisDataVisualizationModule),
    title: 'label.DataVisualization',
  },
  {
    path: 'data-visualization/reports',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('@iris/modules/data-visualization/user-pages/reports/reports-page.routes'),
    title: 'label.Reports',
  },
  {
    path: 'data-visualization/dashboards',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('@iris/modules/data-visualization/user-pages/dashboards/dashboards-page.routers'),
    title: 'label.Dashboards',
  },
  {
    path: 'dms',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/dms/dms.module').then(m => m.DmsModule),
    title: 'label.DocumentManagement',
  },
  {
    path: 'bim',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/bim/bim.module').then(m => m.IrisBimModule),
    data: { moduleCode: IrisModules.BIM },
    title: 'label.BimViewer',
  },
  {
    path: 'bimpact',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/bimpact/bimpact.module').then(m => m.BimpactModule),
    data: { moduleCode: IrisModules.BIM },
    title: 'BIMPACT',
  },
  {
    path: 'bim-actions',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/bim-actions/bim-actions.module').then(m => m.IrisBimActionsModule),
    data: { moduleCode: IrisModules.BIM_ACTIONS },
    title: 'label.BimActions',
  },
  {
    path: 'dashboards',
    canMatch: [isUserAuthorizedGuard],
    canActivate: [canAccessModuleGuard],
    loadChildren: () => import('./iris-modules/dashboards/dashboards.module').then(m => m.DashboardsModule),
    title: 'label.DashboardBeta',
  },
  {
    path: 'styleguide',
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/styleguide/styleguide.module').then(m => m.IrisStyleguideModule),
    title: 'label.Styleguide',
  },
  {
    path: 'dynamic-forms',
    canMatch: [isUserAuthorizedGuard],
    canActivate: [canAccessModuleGuard],
    loadChildren: () => import('./iris-modules/dynamic-forms/dynamic-forms.module').then(m => m.IrisDynamicFormsModule),
    data: { moduleCode: IrisModules.DYNAMIC_FORMS },
  },
  {
    path: 'interval-scanners',
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/interval-scanners/interval-scanners.module').then(m => m.IrisIntervalScannersModule),
    title: 'label.IntervalScanner',
  },
  {
    path: 'forbidden',
    loadChildren: () => import('./iris-modules/forbidden-pages/forbidden-pages.module').then(m => m.IrisForbiddenPagesModule),
  },
  {
    path: 'charts',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/charts/charts.module').then(m => m.IrisChartsModule),
    data: { moduleCode: IrisModules.CHARTS },
    title: 'label.Charts',
  },
  {
    path: 'overview',
    canMatch: [isUserAuthorizedGuard],
    canActivate: [canAccessModuleGuard],
    loadComponent: () => import('./iris-modules/sensor-data-overview/sensor-data-overview-page.component').then(m => m.SensorDataOverviewPage),
    title: 'label.Overview',
  },
  {
    path: 'tunnel-trends',
    canMatch: [isUserAuthorizedGuard],
    canActivate: [canAccessModuleGuard],
    loadComponent: () => import('./iris-modules/sensor-data-trends/sensor-data-trends-page.component').then(m => m.SensorDataTrendsPage),
    title: 'label.Trends',
  },
  {
    path: 'home',
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/home/home.routes').then(r => r.HOME_ROUTER),
    data: { moduleCode: IrisModules.HOME },
    title: 'label.Home',
  },
  {
    path: 'user-notifications',
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/user-notifications/user-notifications.module').then(m => m.IrisUserNotificationsModule),
    title: 'label.UserNotifications',
  },
  {
    path: 'administration',
    canActivate: [canConfigModuleGuard()],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/administration/administration.module').then(m => m.IrisAdministrationModule),
    data: { moduleCode: IrisModules.ADMINISTRATION },
    title: 'label.Administration',
  },
  {
    path: 'dzy/round-list',
    canActivate: [canConfigModuleGuard()],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/round-list/round-list.module').then(m => m.RoundListModule),
    title: 'label.RoundList',
    data: { moduleCode: IrisModules.ROUND_LIST },
  },
  {
    path: 'dzy',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/dzy/dzy.module').then(m => m.IrisDzyModule),
    data: { moduleCode: IrisModules.DZY },
    title: 'label.ShiftReport',
  },
  {
    path: 'emails',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/emails/emails.module').then(m => m.IrisEmailModule),
    data: { moduleCode: IrisModules.EMAIL },
    title: 'label.Email',
  },
  {
    path: 'sia',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/sia/sia.routes').then(r => r.SIA_ROUTES),
    data: { moduleCode: IrisModules.SIA },
    title: 'label.sia.Title',
  },
  {
    path: 'configuration',
    canActivate: [canConfigModuleGuard()],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/system-configuration/system-configuration.module').then(m => m.IrisSystemConfigurationModule),
    data: { moduleCode: IrisModules.SYSTEMCONFIG },
    title: 'label.SystemConfiguration',
  },
  {
    path: 'sensorboards',
    canActivate: [canConfigModuleGuard()],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('@iris/modules/sensorboards/sensorboards.module').then(m => m.SensorboardsModule),
    data: { moduleCode: IrisModules.SENSORBOARD },
    title: 'label.Sensorboards',
  },
  {
    path: 'alarm',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/alarm/alarm.module').then(m => m.AlarmModule),
    data: { moduleCode: IrisModules.ALARMING },
  },
  {
    path: 'sensor-groups',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/sensor-groups/sensor-groups.module').then(m => m.SensorGroupsModule),
    title: 'label.SensorGroups',
    data: { moduleCode: IrisModules.SENSOR_GROUPS },
  },
  {
    path: 'measurement-export',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/measurement-export/measurement-export.module').then(m => m.MeasurementExportModule),
    title: 'label.MeasurementExport',
    data: { moduleCode: IrisModules.MEASUREMENT_EXPORT },
  },
  {
    path: 'sensors-ms',
    canActivate: [canAccessModuleGuard],
    canLoad: [isUserAuthorizedGuard],
    loadChildren: () => import('./iris-modules/sensors-ms/sensors-ms.module').then(m => m.IrisSensorsMsModule),
    data: { moduleCode: IrisModules.SENSORS },
    title: 'label.Sensors',
  },
  {
    path: 'central-project-data',
    canMatch: [isUserAuthorizedGuard],
    canActivate: [featureEnabled(IrisFeatureFlags.CENTRAL_PROJECT_DATA)],
    loadChildren: () => import('./iris-modules/central-project-data/central-project-data.routes').then(x => x.CENTRAL_PROJECT_DATA_ROUTES),
    title: 'label.Cpd',
  },
  {
    path: 'auto-reports',
    canActivate: [canAccessModuleGuard],
    canMatch: [isUserAuthorizedGuard],
    loadChildren: () => import('@iris/modules/auto-reports/auto-reports.routes'),
    title: 'label.GeneratedReports',
  },
];

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./iris-modules/auth/auth.module').then(m => m.AuthModule),
    title: 'label.Login',
  },
  {
    path: 'ui/ui2',
    pathMatch: 'full',
    redirectTo: 'iris',
  },
  {
    path: 'ui/ui2/login',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: 'ui/ui2/:path',
    redirectTo: 'iris/:path',
  },
  {
    path: '',
    canActivate: [isUserAuthorizedGuard, syncSavedAppAliasGuard],
    component: AppComponent,
    children: [
      {
        path: '',
        loadComponent: () => import('./iris-modules/launchpad/launchpad.component').then(m => m.IrisLaunchpadComponent),
        title: 'label.Applications',
      },
      {
        path: ':appAlias',
        children: ROUTES_AFTER_LOGIN,
        canActivate: [applicationExistsGuard],
      },
    ],
    providers: [
      {
        // TODO: after updating "@ngx-translate/core" to a version bigger than 16.0.4 this provide maybe won't be necessary
        provide: TranslateService,
        useFactory: irisTranslationServiceProxy,
      },
    ],
  },
];
@NgModule({
  imports: [RouterModule.forRoot(routes, {
    bindToComponentInputs: true,
    paramsInheritanceStrategy: 'always',
  })],
  providers: [
    AppAliasUrlSerializer,
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
